import { ID_PART_PATTERN } from "./id-grammar.js";
import { safeRuleLabel } from "./fixture-placement-rules.js";
//#region src/fixture-secret-ref-rules.ts
/**
* One component of the app identity, usable for an ownership determination.
*
* ⭐ THE SINGLE PREDICATE (D-9). {@link appOwnedSecretRefs} and {@link hasUsableIdentity} both call
* this, so the set of identities that yields an allowlist and the set that the rule calls "usable"
* are the SAME set BY CONSTRUCTION rather than by agreement. Writing the condition twice is how the
* two drift, and a drift here means {@link notAppOwnedIssue} renders an EMPTY "what is allowed"
* list while claiming a determination.
*
* ⛔ `ID_PART_PATTERN` IS THE WHOLE ANSWER TO THE DERIVATION DIRECTION (D-1.4b / D-5 as amended).
* Exact match stops app `alpha` from NAMING `alpha--beta--api--db-credentials`; it does nothing
* about app `alpha` DERIVING that same name from `fixtureId = 'beta--api'`. The delimiter this
* module interpolates is `--`, so a component containing `--` makes the derivation ambiguous and
* hands one app another app's Secret names. Requiring the grammar the operator itself enforces
* (`id-grammar.ts`) is what closes it - and it closes the leading-digit and trailing-hyphen shapes
* in the same expression, rather than patching the one instance that was noticed.
*
* ⛔ DO NOT RELAX THIS FOR `appKey`. `apps.slug` is WIDER than this grammar, so a legal-but-
* undeployable slug (`9lives`, `alpha--beta`, `alpha-`) is now "unusable" here and every declared
* secret volume of such an app is REFUSED, fail-closed, at both enforcing doors. That is the
* accepted consequence of the ruling, not a bug: an app whose slug fails this pattern can never
* reconcile (`fixture-deployer.ts:1113`), so none of its four owned Secrets is ever created.
*
* ⚠️ NO SEPARATE EMPTY-STRING CHECK IS NEEDED, and its absence is not an omission: the pattern is
* anchored and begins `^[a-z]`, so `ID_PART_PATTERN.test('') === false` (measured). ⛔ Equally, do
* NOT add a length bound: `MAX_FIXTURE_ID_LENGTH` is a fixture-id cap rather than part of the
* grammar, and binding it here would refuse identities the doors accept. The pattern is linear
* (unambiguous alternation, anchored, no nested unbounded quantifier) - measured at 7.5 µs per call
* on a 5 000-char input - so it is safe on unbounded caller input.
*
* A type guard, so the template literals in {@link appOwnedSecretRefs} typecheck with no cast.
*/
function isUsableIdentityComponent(v) {
	return typeof v === "string" && ID_PART_PATTERN.test(v);
}
/**
* Can this call determine ownership AT ALL? (D-9.)
*
* ⛔ BOTH components are required. `appKey` alone names only `` `${appKey}--nats-credentials` `` -
* three of the four owned names need `fixtureId` too - so a usable `appKey` with an empty
* `fixtureId` cannot support the claim "this Secret is not owned by this app".
*/
function hasUsableIdentity(appKey, fixtureId) {
	return isUsableIdentityComponent(appKey) && isUsableIdentityComponent(fixtureId);
}
/**
* The FOUR Secret names an app+fixture owns (D-5). Every one traces to the single operator helper
* that CREATES that Secret, so this list is auditable against four named functions rather than
* against a pattern:
*
* | name | created by |
* |---|---|
* | `` `${appKey}--${fixtureId}--db-credentials` ``       | `db-credential-reconciler.ts:242` |
* | `` `${appKey}--${fixtureId}--platform-secrets` ``     | `platform-secret-reconciler.ts:97` |
* | `` `${appKey}--${fixtureId}--svc-nats-credentials` `` | `svc-cred-secret-reconciler.ts:127` |
* | `` `${appKey}--nats-credentials` ``                   | `nats-credential-reconciler.ts:183` |
*
* ⛔ TWO DIRECTIONS, AND BOTH MUST BE CLOSED (D-5 as amended, D-1.4b):
*   - MATCH direction - closed by exact string equality below: app `alpha` may not NAME
*     `alpha--beta--api--db-credentials`.
*   - DERIVATION direction - closed by `isUsableIdentityComponent`'s `ID_PART_PATTERN` test: app
*     `alpha` may not DERIVE that same name by passing `fixtureId = 'beta--api'`. Interpolating a
*     component that itself contains the `--` delimiter is ambiguous, and the ambiguity hands one
*     app three of another app's four Secret names.
* ⭐ Closing only the first and calling the class closed is the error this docblock exists to stop
* anyone repeating.
*
* ⛔ EXACT MATCH, NOT A PREFIX AND NOT A REGEX (D-5). Two of the three reasons are defects a prefix
* rule would have shipped:
*   1. `apps.slug`'s grammar is `[a-z0-9-]`, which permits CONSECUTIVE HYPHENS, so `alpha--beta` is
*      a legal slug. App `alpha` checked against the prefix `alpha--` admits
*      `alpha--beta--api--db-credentials`, which belongs to a DIFFERENT APP.
*   2. `appKey` is not guaranteed regex-clean: the legacy envelope path admits dot-bearing keys
*      (pinned as behaviour at `operator-api.service.spec.ts:1322-1324`) and UUID-shaped slugs
*      remain creatable (#855). Interpolating such a value into a `RegExp` is a metacharacter bug
*      at best. String equality is immune to both.
*   3. Four literals traceable to four named helpers are auditable; a pattern is not.
*
* ⛔ NO NORMALISATION ANYWHERE. Kubernetes compares Secret names byte-wise, so this comparison must
* too: no `toLowerCase()`, no `trim()`, no `String.prototype.normalize()`, on either side. Any of
* those creates a value that PASSES this rule and NAMES A DIFFERENT SECRET.
*
* ⛔ DELIBERATELY EXCLUDED, each fail-closed (D-5):
*   - `` `${appKey}--${svcName}--${versionSlug}-secrets` `` - SYNTHESIZED by the operator after
*     validation, version-dependent (it would rot on every version bump), and no Secret of that
*     name is ever actually created (`ensureExternalSecret`, `fixture-deployer.ts:4876-4880`, is a
*     stub whose entire body is a debug log). An app declaring it would mount nothing.
*   - `` `${handle}--nats-credentials` `` - per-compute-unit, brokered and mounted by the operator.
*   - `` `${appId}--db-credentials` `` - the legacy CP-provisioned per-app Secret
*     (`contracts-cp-dp/src/app-db-credentials.ts:25`). Zero apps reference it and whether it is
*     keyed by slug or UUID is unverified. Adding it later is a deliberate one-line act.
*   - Every platform Secret - `core-api-secrets`, `dp-llm-secrets`, `cp-secrets`,
*     `zion-dp-client-tls`, `zion-operator-nats-creds`. These are the targets.
*
* ⭐ EXPORTED so callers and tests DERIVE the set instead of restating it. A consumer that
* hand-copies these four templates is a fifth place they can drift.
*
* @param appKey    The app's REGISTERED SLUG - `appEntity.slug` at CP publish, the `desired.apps`
*                  map key at operator reconcile (D-3). ⛔ NEVER `manifest.appId`: that is a
*                  DIFFERENT FIELD with a different grammar, it is attacker-supplied, and nothing
*                  in the codebase compares the two.
* @param fixtureId The fixture's id - the `desired.apps[appKey].fixtures` loop key at reconcile.
* @returns The four owned names, in a fixed order; or `[]` when either argument is not a usable
*          identity component - i.e. not a string matching `ID_PART_PATTERN` (D-1.4b), which
*          excludes the empty string, any `--`-bearing value, a leading digit and a trailing
*          hyphen. An empty result means "ownership is UNPROVABLE here", which
*          {@link checkFixtureSecretRefs} reports as `secret-ref-ownership-unverified` - it never
*          means "everything is allowed".
*/
function appOwnedSecretRefs(appKey, fixtureId) {
	if (!isUsableIdentityComponent(appKey)) return [];
	if (!isUsableIdentityComponent(fixtureId)) return [];
	return [
		`${appKey}--${fixtureId}--db-credentials`,
		`${appKey}--${fixtureId}--platform-secrets`,
		`${appKey}--${fixtureId}--svc-nats-credentials`,
		`${appKey}--nats-credentials`
	];
}
/**
* The allowlist SHAPE, rendered with placeholder arguments, for the message that has no identity to
* work with.
*
* ⭐ Derived by CALLING {@link appOwnedSecretRefs} rather than hand-written a second time. A
* hand-written twin is a comment restating the code, and a mutation to a literal would leave it
* untouched - the exact failure `guard-tests-must-be-mutation-checked` names. Deriving it makes
* `WS1-04` a third independent detector of an altered allowlist literal.
*
* ⛔ THE PLACEHOLDERS MUST THEMSELVES BE LEGAL ID PARTS, and that is a consequence of D-1.4b, not a
* style choice. This constant read `appOwnedSecretRefs('<appKey>', '<fixtureId>')` before the D-5
* amendment; `'<appKey>'` does not match `ID_PART_PATTERN`, so that call now returns `[]` and the
* warning would render an EMPTY "what is allowed" list - the exact unfalsifiable message D-9 exists
* to prevent, arriving through the derivation this decision tightened. `your-app-slug` and
* `your-fixture-id` match the grammar (lowercase, single hyphens, letter-initial) and still read as
* placeholders. ⛔ Do not restore the angle-bracket form, and do not hand-write the four shapes to
* dodge this - either move deletes `WS1-04`'s third reading of the mutation proof.
*/
const OWNERSHIP_SHAPE_EXAMPLE = appOwnedSecretRefs("your-app-slug", "your-fixture-id");
/**
* Prefer a declared name for a message; fall back to the JSON pointer when the name is absent or
* non-string (the manifest is `unknown` - these rules run even when schema validation failed).
*
* ⭐ NOT A DUPLICATE of `fixture-placement-rules.ts`'s private helper of the same name (D-8): this
* is a one-line SELECTOR that delegates every sanitised byte to the imported {@link safeRuleLabel}.
* There is one sanitiser in this package and this function is not a second one.
*
* ⛔ Sanitising HERE covers every call site at once - the unit NAME and the volume NAME are
* app-authored too, and at the CLI door these rules run EVEN WHEN SCHEMA VALIDATION FAILED, which
* is exactly the path a hostile name arrives by.
*/
function nameOrPath(name, fallbackPath) {
	return typeof name === "string" ? safeRuleLabel(name) : fallbackPath;
}
/** Render a list of Secret names for a message. Each entry goes through {@link safeRuleLabel}
*  because the allowlist INTERPOLATES `appKey` and `fixtureId`, which are untrusted at the CLI
*  door - an unsanitised list would echo four copies of a hostile identity. */
function renderNames(names) {
	return names.map((n) => `'${safeRuleLabel(n)}'`).join(", ");
}
/**
* #1506 - the refusal. `error`, so both enforcing doors fail closed on it.
*
* The message names the offending value AND lists what IS allowed, which is an acceptance criterion
* (`overview.md` §4), and it points at the supported way to get a file-mounted Secret so the author
* is not left guessing. Both untrusted inputs are sanitised: the offending value, and every entry
* of `allowed` (each interpolates `appKey`/`fixtureId`), so the whole message is bounded at roughly
* five 67-char fragments plus fixed prose.
*/
function notAppOwnedIssue(path, label, secretRef, allowed) {
	return {
		severity: "error",
		path,
		rule: "secret-ref-not-app-owned",
		message: `${label} declares a 'secret' volume whose 'secretRef' is '${safeRuleLabel(secretRef)}', which is NOT a Secret this app owns. A fixture pod shares its namespace with the platform's own pods, so an unconstrained 'secretRef' file-mounts ANY Secret in that namespace - including 'core-api-secrets', which carries the data plane's internal service key. This app and fixture may mount exactly these Secrets: ${renderNames(allowed)}. To get a file-mounted Secret of your own, declare it under 'config.secrets[*].mountPath' and the platform provisions the Secret and mounts it for you - do not name a Secret by hand.`
	};
}
/**
* #1506 - the caller could not supply a usable identity, so the rule REPORTS rather than admits.
*
* ⛔ THIS BRANCHES ON THE INPUT, NEVER ON THE CALLING DOOR (D-4a). This module is pure: it cannot
* know which door called it, and it takes no door parameter, no severity option and no
* `identityIsAuthoritative` flag - all three were considered and REJECTED, because a flag that
* changes output semantics moves door policy into shared code where no door can see it. The only
* thing the producer can branch on is whether `(appKey, fixtureId)` is a usable identity - both
* components strings matching `ID_PART_PATTERN` (D-1.4b).
* ⇒ **the producer states what is true; a consumer decides how loudly to act on it given how far it
* trusts its own identity input.**
*
* ⛔ THIS IS NOT AN ADMISSION. One issue is emitted per declared `secret` volume, so a caller that
* ignores warnings is making a decision, not inheriting a silence.
*
* ⚠️ IN PRACTICE NO WIRED DOOR REACHES THIS ARM, and that is by design (D-4a). All three doors pass
* a usable identity: CP publish passes `appEntity.slug` (the DB row), the operator passes `appKey`
* (the `desired.apps` map key), and the CLI passes `app.json`'s `appId` and DOWNGRADES
* `secret-ref-not-app-owned` to a warning in its own named adapter - because `appId` and
* `apps.slug` are different fields with different grammars and nothing ties them (D-3). ⛔ Do NOT
* conclude this arm is dead and delete it: it is the fail-closed answer for any FUTURE caller that
* cannot supply an identity, and deleting it would make an empty allowlist read as "no constraint",
* which admits every secret volume. `WS1-20` is the test that pins it.
*
* ⛔ DELIBERATELY NOT WIRED to `zion-app validate <fixture.json>` (D-4): that path validates a
* single fixture in isolation and never reads `app.json`, so it can supply no identity at all. The
* omission is recorded here rather than left silent.
*/
function ownershipUnverifiedIssue(path, label, secretRef, appKey, fixtureId) {
	return {
		severity: "warning",
		path,
		rule: "secret-ref-ownership-unverified",
		message: `${label} declares a 'secret' volume whose 'secretRef' is '${safeRuleLabel(secretRef)}', but this door cannot prove which Secrets the app owns: the app identity it was given is not a usable pair of id parts (appKey '${safeRuleLabel(appKey)}', fixtureId '${safeRuleLabel(fixtureId)}') - each part must be lowercase, start with a letter, and use single hyphens only. An app may mount only ${renderNames(OWNERSHIP_SHAPE_EXAMPLE)}. CP publish and the operator both know the registered app slug and WILL refuse this value if it is not one of those - so treat this as a publish-blocking finding, not advice.`
	};
}
/**
* The verdict for ONE declared `secret` volume.
*
* ⛔ D-9 - THE IDENTITY CHECK COMES FIRST, AND IT IS AN EXPLICIT CHECK. The two rule ids are not
* severity tiers of one finding; they are DIFFERENT CLAIMS:
*   - `secret-ref-not-app-owned`        = "I determined ownership, and this Secret is not owned."
*   - `secret-ref-ownership-unverified` = "I could not determine ownership at all."
* With an unusable identity the second is true and **the first is a FALSE STATEMENT** - its message
* names an app this rule never established, and it lists an allowlist it could not build. A control
* that reports a determination it did not make is worse than one that reports nothing: the message
* is unfalsifiable and reads as authoritative.
*
* ⛔ DO NOT REWRITE THIS AS `if (allowed.length === 0)`. That derivation produces the same door
* OUTCOME today - so no test can catch it, which is exactly why it is written down here - but it
* gets the CLAIM right only by accident, and it couples this function's semantics to an
* implementation detail of {@link appOwnedSecretRefs}. The day that function gains a name that does
* not need `fixtureId`, the derivation starts emitting a false determination.
*
* ⛔ THERE IS NO SKIP ARM, and that is the point (security-audit.md §3.5). An absent, non-string or
* empty `secretRef` on a `type: 'secret'` volume is REFUSED, not passed over: `typeof !== 'string'
* -> continue` is a fail-open branch, and `volumeToK8s` throws on exactly that shape at reconcile
* time anyway (`fixture-pod-template-builder.ts:405`), so refusing here converts a per-fixture
* reconcile throw into a publish-time message.
*
* @param identityIsUsable {@link hasUsableIdentity} for this call. Passed in rather than recomputed
*                         per volume - it is constant across the whole manifest.
* @returns the issue, or `null` when the volume names a Secret this app owns.
*/
function checkOneSecretVolume(path, label, secretRef, appKey, fixtureId, identityIsUsable, allowed) {
	if (!identityIsUsable) return ownershipUnverifiedIssue(path, label, secretRef, appKey, fixtureId);
	const ref = typeof secretRef === "string" ? secretRef : null;
	if (ref !== null && allowed.includes(ref)) return null;
	return notAppOwnedIssue(path, label, secretRef, allowed);
}
/**
* The ONE entry point all three doors call (`00-index.md` §2).
*
* Walks `services[].volumes[]` and reports every declared `type: 'secret'` volume whose `secretRef`
* is not one of the four names {@link appOwnedSecretRefs} derives for `(appKey, fixtureId)`.
*
* ⛔ `services[]` IS THE COMPLETE SURFACE, measured, not assumed (D-1.1). In
* `schemas/fixture.schema.json`, `volumes` appears only under `$defs/service` (`:759`, plus a
* back-reference in that def's own `allOf` at `:880`); `$defs/worker`, `$defs/job`,
* `$defs/container`, `$defs/initContainer` and the document root all declare
* `"additionalProperties": false` and none lists `volumes`. In the TS contract, `volumes` occurs on
* exactly one interface (`fixture-manifest.ts:772`, `FixtureService`). In the operator, every
* manifest-sourced read is service-scoped (`fixture-pod-template-builder.ts:1010`,
* `reconciler.ts:4205`, `dedicated-pool-compiler.ts:338`). ⛔ IF YOU ADD `volumes` TO ANOTHER DEF,
* `fixture-secret-ref-rules.test.ts` `WS1-30` goes red and names this function - extend the walk
* there, do not silence the test.
*
* ⛔ THE `type` GATE IS EXACT. `volumeToK8s` reads `v.type ?? 'pvc'` and mounts a Secret only on
* the exact string `'secret'` (`fixture-pod-template-builder.ts:398-412`), so an absent `type`, a
* `null`, a number, `'pvc'` and `'emptyDir'` cannot name a Secret and are correctly not our
* business. Widening this test to "anything that is not pvc" would refuse legal `emptyDir` volumes.
*
* Rules emitted:
*   - `secret-ref-not-app-owned`        (`error`)   - the value is outside the allowlist, or is
*                                                     absent / non-string / empty on a declared
*                                                     `secret` volume.
*   - `secret-ref-ownership-unverified` (`warning`) - the identity given cannot prove ownership.
*
* @param manifest  The FIXTURE MANIFEST as authored. `unknown` - these rules run even when schema
*                  validation FAILED, so every access is guarded. ⛔ NOT a rendered pod spec and
*                  NOT the operator's merged volume list (D-1).
* @param appKey    The app's registered SLUG (D-3). ⛔ Never `manifest.appId`.
* @param fixtureId The fixture id.
*
* @example
* // release-engine's publish door (WS-2), identity from the DB row:
* const issues = checkFixtureSecretRefs(fixtureManifest, appEntity.slug, fixtureManifest.fixtureId);
* // zion-operator's reconcile door (WS-3), identity from the desired-state map key:
* const issues = checkFixtureSecretRefs(fixture.manifest, appKey, fixtureId);
*/
function checkFixtureSecretRefs(manifest, appKey, fixtureId) {
	if (manifest === null || typeof manifest !== "object") return [];
	const m = manifest;
	const identityIsUsable = hasUsableIdentity(appKey, fixtureId);
	const allowed = appOwnedSecretRefs(appKey, fixtureId);
	const issues = [];
	const services = Array.isArray(m.services) ? m.services : [];
	for (let si = 0; si < services.length; si++) {
		const svc = services[si];
		if (svc === null || typeof svc !== "object") continue;
		const s = svc;
		const svcPath = `/services/${si}`;
		const svcLabel = `service '${nameOrPath(s.name, svcPath)}'`;
		const volumes = Array.isArray(s.volumes) ? s.volumes : [];
		for (let vi = 0; vi < volumes.length; vi++) {
			const vol = volumes[vi];
			if (vol === null || typeof vol !== "object") continue;
			const v = vol;
			if (v.type !== "secret") continue;
			const volPath = `${svcPath}/volumes/${vi}`;
			const label = `${svcLabel} volume '${nameOrPath(v.name, volPath)}'`;
			const issue = checkOneSecretVolume(`${volPath}/secretRef`, label, v.secretRef, appKey, fixtureId, identityIsUsable, allowed);
			if (issue !== null) issues.push(issue);
		}
	}
	return issues;
}
//#endregion
export { appOwnedSecretRefs, checkFixtureSecretRefs };

//# sourceMappingURL=fixture-secret-ref-rules.js.map